import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';

import { QUESTION_LOGIC_OPERATORS, QUESTION_TYPES } from '@api/instant_survey';
import {
  Button,
  Card,
  ErrorText,
  isBetweenMinAndMax,
  Layout,
  ModalV2 as Modal,
  PlusPlainIcon,
  sanitizeRichText,
  TrashIcon,
} from '@utilities';

import OptionLogic from './components/OptionLogic';

import styles from './_index.module.scss';

const ModalAnswerOptionLogic = ({
  onClose,
  question,
  questionIndex,
  questionLogic,
  selectedQuestions,
  setQuestionLogic,
}) => {
  const [localQuestionLogic, setLocalQuestionLogic] = useState(cloneDeep(questionLogic));
  const skipOperators = [QUESTION_LOGIC_OPERATORS.ALL, QUESTION_LOGIC_OPERATORS.ANY];
  const firstErrorRef = useRef(null);

  // Grid questions apply option logic to their rows (subQuestions) rather than
  // their columns (answers). All downstream logic is keyed by item id, so we
  // just feed the correct list of items into the map below.
  const isGrid = question.type === QUESTION_TYPES['TABLE-RADIO'].value;
  const optionItems = isGrid ? question.subQuestions : question.answers;

  const getInitialExpandedAnswers = () => {
    const expanded = new Set();
    if (questionLogic[question.id]?.option) {
      Object.keys(questionLogic[question.id].option).forEach((answerId) => {
        expanded.add(answerId);
      });
    }
    return expanded;
  };
  const [expandedAnswerIds, setExpandedAnswerIds] = useState(getInitialExpandedAnswers);

  const hasAnswerOptionErrors = (answerId) => {
    const optionLogic = localQuestionLogic[question.id]?.option?.[answerId];
    if (!optionLogic?.logic) return false;

    return optionLogic.logic.some((row) => row.hasAnswerError || row.hasQuestionError);
  };

  const handleSave = () => {
    const modifiedQuestionLogic = cloneDeep(localQuestionLogic);
    let isValid = true;

    if (modifiedQuestionLogic[question.id]?.option) {
      Object.keys(modifiedQuestionLogic[question.id].option).forEach((answerId) => {
        const optionLogic = modifiedQuestionLogic[question.id].option[answerId];

        if (optionLogic?.logic) {
          optionLogic.logic.forEach((row) => {
            // For quota group logic
            if (Object.prototype.hasOwnProperty.call(row, 'url_variable')) {
              if (!row.values || row.values.length === 0) {
                row.hasAnswerError = true;
                row.isFirstAnswerError = isValid;
                isValid = false;
              } else {
                row.hasAnswerError = false;
                row.isFirstAnswerError = false;
              }
            }
            // For answer-based logic
            else {
              if (!row.question || !row.question.id) {
                row.hasQuestionError = true;
                row.isFirstQuestionError = isValid;
                isValid = false;
              } else {
                row.hasQuestionError = false;
                row.isFirstQuestionError = false;
              }

              // Validate answer/comparison value if question is selected
              if (row.question?.id) {
                if (row.question.type === QUESTION_TYPES.NUMERIC.value) {
                  // Validate numeric comparison value
                  if (
                    row.comparison_value === undefined ||
                    row.comparison_value === null ||
                    row.comparison_value === '' ||
                    !isBetweenMinAndMax({
                      max: row.question.properties?.maxNumber,
                      min: row.question.properties?.minNumber,
                      value: row.comparison_value,
                    })
                  ) {
                    row.hasAnswerError = true;
                    row.isFirstAnswerError = isValid;
                    isValid = false;
                  } else {
                    row.hasAnswerError = false;
                    row.isFirstAnswerError = false;
                  }
                } else {
                  // Validate answer selection
                  if (!row.answers || row.answers.length === 0) {
                    row.hasAnswerError = true;
                    row.isFirstAnswerError = isValid;
                    isValid = false;
                  } else {
                    row.hasAnswerError = false;
                    row.isFirstAnswerError = false;
                  }
                }
              } else {
                // If no question is selected, also check if answers are blank
                if (!row.answers || row.answers.length === 0) {
                  row.hasAnswerError = true;
                  row.isFirstAnswerError = isValid;
                  isValid = false;
                } else {
                  row.hasAnswerError = false;
                  row.isFirstAnswerError = false;
                }
              }
            }
          });
        }
      });
    }

    if (!isValid) {
      setLocalQuestionLogic(modifiedQuestionLogic);
      setTimeout(() => {
        firstErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }

    setQuestionLogic(modifiedQuestionLogic);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const toggleAnswerExpansion = (rawAnswerId) => {
    const answerId = String(rawAnswerId);
    setExpandedAnswerIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(answerId)) {
        newSet.delete(answerId);
      } else {
        newSet.add(answerId);
        if (!localQuestionLogic[question.id]?.option?.[answerId]) {
          const updatedLogic = { ...localQuestionLogic };
          const existingQuestionLogic = updatedLogic[question.id] || {};
          const initialRow =
            questionIndex === 0
              ? {
                  operator: QUESTION_LOGIC_OPERATORS.ANY,
                  url_variable: 'instant_survey_group_ref',
                  values: [],
                }
              : {
                  answers: [],
                  operator: QUESTION_LOGIC_OPERATORS.ANY,
                  question: null,
                };
          updatedLogic[question.id] = {
            ...existingQuestionLogic,
            option: {
              ...(existingQuestionLogic.option || {}),
              [answerId]: {
                logic: [initialRow],
                operator: skipOperators[0],
              },
            },
          };
          setLocalQuestionLogic(updatedLogic);
        }
      }
      return newSet;
    });
  };

  const removeOptionLogic = (rawAnswerId) => {
    const answerId = String(rawAnswerId);
    const updatedLogic = cloneDeep(localQuestionLogic);

    if (updatedLogic[question.id]?.option?.[answerId]) {
      delete updatedLogic[question.id].option[answerId];

      // Clean up empty option object
      if (Object.keys(updatedLogic[question.id].option).length === 0) {
        delete updatedLogic[question.id].option;
      }

      setLocalQuestionLogic(updatedLogic);
    }

    setExpandedAnswerIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(answerId);
      return newSet;
    });
  };

  return (
    <Modal
      buttons={[
        <Button key="cancel" onClick={handleCancel} text="Cancel" variant="secondary" />,
        <Button key="save" onClick={handleSave} text="Save" />,
      ]}
      className={styles['modal-answer-option-logic']}
      enforceFocus={false}
      isActive
      onClose={handleCancel}
      size="large"
      title="Answer Options Logic"
    >
      <Layout.Flex.Column gap="medium">
        <p>By default, all answer options are displayed.</p>
        <div className={styles['modal-answer-option-logic-list']}>
          {optionItems.map((answer) => {
            const isExpanded = expandedAnswerIds.has(String(answer.id));
            const hasLogic = localQuestionLogic[question.id]?.option?.[answer.id];
            return (
              <Card className={styles['modal-answer-option-logic-card']} key={answer.id}>
                <div className={styles['modal-answer-option-logic-option']}>
                  <div
                    className={styles['modal-answer-option-logic-option-text']}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeRichText(answer.richText || answer.text),
                    }}
                  />
                  <button
                    className={styles['modal-answer-option-logic-icon-button']}
                    data-testid={`answer-option-button-${answer.id}`}
                    onClick={() =>
                      isExpanded && hasLogic
                        ? removeOptionLogic(answer.id)
                        : toggleAnswerExpansion(answer.id)
                    }
                    type="button"
                  >
                    {isExpanded && hasLogic ? <TrashIcon /> : <PlusPlainIcon />}
                  </button>
                </div>
                {isExpanded && (
                  <OptionLogic
                    answer={answer}
                    firstErrorRef={firstErrorRef}
                    question={question}
                    questionIndex={questionIndex}
                    questionLogic={localQuestionLogic}
                    selectedQuestions={selectedQuestions}
                    setQuestionLogic={setLocalQuestionLogic}
                  />
                )}
                {isExpanded && hasAnswerOptionErrors(answer.id) && (
                  <div className={styles['modal-answer-option-logic-error']}>
                    <ErrorText text="The logic cannot be applied because there is an error." />
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </Layout.Flex.Column>
    </Modal>
  );
};

ModalAnswerOptionLogic.propTypes = {
  onClose: PropTypes.func.isRequired,
  question: PropTypes.object.isRequired,
  questionIndex: PropTypes.number.isRequired,
  questionLogic: PropTypes.object.isRequired,
  selectedQuestions: PropTypes.array.isRequired,
  setQuestionLogic: PropTypes.func.isRequired,
};

export default ModalAnswerOptionLogic;
